// client/src/components/admin/Users.tsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import SearchIcon from '@mui/icons-material/Search';

interface User {
  _id?: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'administrator' | 'assessor';
  active: boolean;
  lastLogin?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

interface UsersProps {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

type SortField = 'name' | 'email' | 'role' | 'lastLogin' | 'status';
type SortDirection = 'asc' | 'desc';

const sortLabelSx = {
  color: 'rgba(255, 255, 255, 0.9) !important',
  '&:hover': { color: 'rgba(255, 255, 255, 1) !important' },
  '&.Mui-active': { color: 'rgba(100, 181, 246, 1) !important' },
  '& .MuiTableSortLabel-icon': { color: 'rgba(100, 181, 246, 0.9) !important' }
};

const formatDate = (date: string | Date | undefined) => {
  if (!date) return 'Never';
  return new Date(date).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const UserList: React.FC<{
  users: User[];
  onToggle: (user: User) => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onResetPassword: (user: User) => void;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}> = ({ users, onToggle, onEdit, onDelete, onResetPassword, sortField, sortDirection, onSort }) => {
  if (!Array.isArray(users) || users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography color="text.secondary">No users found</Typography>
      </Box>
    );
  }

  const headerCellSx = {
    color: 'rgba(255, 255, 255, 0.9)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const sortableCell = (field: SortField, label: string) => (
    <TableCell sx={headerCellSx}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDirection : 'asc'}
        onClick={() => onSort(field)}
        sx={sortLabelSx}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <TableContainer
      component={Paper}
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
      }}
    >
      <Table>
        <TableHead sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
          <TableRow>
            {sortableCell('name', 'Name')}
            {sortableCell('email', 'Email')}
            {sortableCell('role', 'Role')}
            {sortableCell('lastLogin', 'Last Login')}
            {sortableCell('status', 'Account Status')}
            <TableCell align="right" sx={headerCellSx}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user._id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{`${user.firstName} ${user.lastName}`}</TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{user.email}</TableCell>
              <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Chip
                  label={user.role}
                  color={user.role === 'administrator' ? 'error' : 'primary'}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              </TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                {formatDate(user.lastLogin)}
              </TableCell>
              <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="body2"
                    color={user.active ? 'success.main' : 'error.main'}
                  >
                    {user.active ? 'Active' : 'Inactive'}
                  </Typography>
                  <Switch
                    checked={user.active}
                    onChange={() => onToggle(user)}
                    color="primary"
                    size="small"
                  />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <IconButton
                  size="small"
                  onClick={() => onEdit(user)}
                  title="Edit user"
                  sx={{ mr: 1, color: 'rgba(100, 181, 246, 0.9)', '&:hover': { color: 'rgba(100, 181, 246, 1)' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onResetPassword(user)}
                  title="Reset password"
                  sx={{ mr: 1, color: 'rgba(255, 183, 77, 0.9)', '&:hover': { color: 'rgba(255, 183, 77, 1)' } }}
                >
                  <LockResetIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDelete(user)}
                  title="Delete user"
                  sx={{ color: 'rgba(239, 83, 80, 0.9)', '&:hover': { color: 'rgba(239, 83, 80, 1)' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const Users: React.FC<UsersProps> = ({ onSuccess, onError }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    role: 'administrator' | 'assessor';
    active: boolean;
  }>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'assessor',
    active: true
  });
  const [formErrors, setFormErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const errors: { firstName?: string; lastName?: string; email?: string } = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    const emailError = validateEmail(formData.email);
    if (emailError) {
      errors.email = emailError;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEmailChange = (email: string) => {
    setFormData({ ...formData, email });
    // Clear email error when user starts typing
    if (formErrors.email) {
      setFormErrors({ ...formErrors, email: undefined });
    }
  };

  const loadUsers = useCallback(async () => {
    try {
      const response = await api.get('/api/users');
      // Handle both paginated response and array response for backward compatibility
      if (Array.isArray(response.data)) {
        setUsers(response.data);
      } else if (response.data && Array.isArray(response.data.users)) {
        setUsers(response.data.users);
      } else {
        console.error('Invalid users data format:', response.data);
        setUsers([]);
        onError('Failed to load users: Invalid data format');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
      onError('Failed to load users');
    }
  }, [onError]);

  // Initial load with loading state
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await loadUsers();
      setLoading(false);
    };
    loadInitialData();
  }, [loadUsers]);

  // Auto-refresh every 30 seconds (silent, no loading state)
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      loadUsers();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [loadUsers]);

  const handleToggleStatus = async (user: User) => {
    try {
      const response = await api.patch(`/admin/users/${user._id}/status`, { active: !user.active });
      const updatedUser = response.data;
      
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u._id === updatedUser._id ? updatedUser : u
        )
      );
      
      onSuccess(`Successfully ${updatedUser.active ? 'activated' : 'deactivated'} user account`);
    } catch (error) {
      console.error('Error toggling user status:', error);
      onError('Failed to toggle user status');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      active: user.active
    });
    setOpenDialog(true);
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Are you sure you want to delete ${user.email}?`)) return;

    try {
      await api.delete(`/api/admin/users/${user._id}`);
      setUsers(prevUsers => prevUsers.filter(u => u._id !== user._id));
      onSuccess('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      onError('Failed to delete user');
    }
  };

  const handleResetPassword = async (user: User) => {
    try {
      await api.post('/api/users/forgot-password', { email: user.email });
      onSuccess('Password reset email has been sent');
    } catch (error) {
      console.error('Error resetting password:', error);
      onError('Failed to reset password');
    }
  };

  const roles = Array.from(new Set(users.map(u => u.role))).sort();

  const filteredUsers = users.filter(user => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch = !term ||
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term);
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' ? user.active : !user.active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortKey = (user: User): string => {
    switch (sortField) {
      case 'name': return `${user.firstName} ${user.lastName}`.toLowerCase();
      case 'email': return user.email.toLowerCase();
      case 'role': return user.role;
      case 'status': return user.active ? 'active' : 'inactive';
      default: return '';
    }
  };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    if (sortField === 'lastLogin') {
      const av = a.lastLogin ? new Date(a.lastLogin).getTime() : null;
      const bv = b.lastLogin ? new Date(b.lastLogin).getTime() : null;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;  // 'Never' always sorts last
      if (bv === null) return -1;
      return (av - bv) * dir;
    }
    return getSortKey(a).localeCompare(getSortKey(b)) * dir;
  });

  const filterControlSx = {
    minWidth: 160,
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#6366f1' },
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
      '&.Mui-focused fieldset': { borderColor: '#6366f1' },
      backgroundColor: 'rgba(255, 255, 255, 0.05)'
    },
    '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
  };

  const filterMenuProps = {
    PaperProps: {
      sx: {
        backgroundColor: '#1e3a8a',
        color: 'white',
        '& .MuiMenuItem-root': {
          textTransform: 'capitalize',
          '&:hover': { backgroundColor: '#2563eb' },
          '&.Mui-selected': { backgroundColor: '#4f46e5' },
          '&.Mui-selected:hover': { backgroundColor: '#4338ca' }
        }
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" className="theme-text-primary">User Management</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingUser(null);
            setFormData({
              firstName: '',
              lastName: '',
              email: '',
              role: 'assessor',
              active: true
            });
            setFormErrors({});
            setOpenDialog(true);
          }}
        >
          ADD USER
        </Button>
      </Box>

      {/* Search & column filters */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              flex: 1,
              minWidth: 220,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              },
              '& .MuiInputBase-input::placeholder': { color: 'rgba(255, 255, 255, 0.5)', opacity: 1 }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                </InputAdornment>
              )
            }}
          />
          <FormControl size="small" sx={filterControlSx}>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleFilter}
              label="Role"
              onChange={(e: SelectChangeEvent) => setRoleFilter(e.target.value)}
              MenuProps={filterMenuProps}
            >
              <MenuItem value="all">All Roles</MenuItem>
              {roles.map(role => (
                <MenuItem key={role} value={role} sx={{ textTransform: 'capitalize' }}>{role}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={filterControlSx}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
              MenuProps={filterMenuProps}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            {filteredUsers.length} of {users.length} users
          </Typography>
        </Box>
      </Paper>
        
      <Dialog 
        open={openDialog} 
        onClose={() => {
          setOpenDialog(false);
          setFormErrors({});
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'white',
          fontSize: '1.5rem',
          fontWeight: 600,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          {editingUser ? 'Edit User' : 'Add User'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  placeholder="user@example.com"
                  sx={{
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#6366f1' },
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    },
                    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={(e) => {
                    setFormData({ ...formData, firstName: e.target.value });
                    if (formErrors.firstName) {
                      setFormErrors({ ...formErrors, firstName: undefined });
                    }
                  }}
                  error={!!formErrors.firstName}
                  helperText={formErrors.firstName}
                  sx={{
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#6366f1' },
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    },
                    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={(e) => {
                    setFormData({ ...formData, lastName: e.target.value });
                    if (formErrors.lastName) {
                      setFormErrors({ ...formErrors, lastName: undefined });
                    }
                  }}
                  error={!!formErrors.lastName}
                  helperText={formErrors.lastName}
                  sx={{
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#6366f1' },
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    },
                    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-focused': { color: '#6366f1' } }}>
                    Role
                  </InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e: SelectChangeEvent) => setFormData({ ...formData, role: e.target.value as 'administrator' | 'assessor' })}
                    name="role"
                    label="Role"
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                      '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: '#1e3a8a',
                          color: 'white',
                          '& .MuiMenuItem-root': {
                            '&:hover': { backgroundColor: '#2563eb' },
                            '&.Mui-selected': { backgroundColor: '#4f46e5' },
                            '&.Mui-selected:hover': { backgroundColor: '#4338ca' }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="administrator">Administrator</MenuItem>
                    <MenuItem value="assessor">Assessor</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          pt: 2,
          px: 3,
          pb: 2
        }}>
          <Button 
            onClick={() => {
              setOpenDialog(false);
              setFormErrors({});
            }}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#6366f1',
              color: 'white',
              '&:hover': { backgroundColor: '#4f46e5' },
              '&:disabled': { backgroundColor: 'rgba(99, 102, 241, 0.5)' }
            }}
            onClick={async () => {
              // Validate form before submitting
              if (!validateForm()) {
                return;
              }

              try {
                if (editingUser) {
                  const response = await api.put(`/api/users/${editingUser._id}`, formData);
                  setUsers(prevUsers =>
                    prevUsers.map(u =>
                      u._id === editingUser._id ? response.data : u
                    )
                  );
                  onSuccess('User updated successfully');
                } else {
                  const response = await api.post('/api/users/create', formData);
                  setUsers([...users, response.data]);
                  onSuccess('User added successfully');
                }
                setOpenDialog(false);
                setEditingUser(null);
                setFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  role: 'assessor',
                  active: true
                });
                setFormErrors({});
              } catch (error) {
                console.error('Error saving user:', error);
                onError('Failed to save user');
              }
            }}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Users List */}
      <UserList
        users={sortedUsers}
        onToggle={handleToggleStatus}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onResetPassword={handleResetPassword}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
      />
    </Box>
  );
};

export default Users;